import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { EbwModalService } from '@ebw-monorepo/modal';
import { TranslateService } from '@ngx-translate/core';
import { MapBrowserEvent } from 'ol';
import { transform } from 'ol/proj';
import { lastValueFrom, Subscription } from 'rxjs';
import { MapService } from '../../../../services/map.service';
import { PsaService } from '../../../../services/psa.service';

/**
 * DetailNodeComponent
 *
 * Componente per la creazione/modifica manuale di un nodo dello schema idropotabile.
 * Stesso pattern di DetailEventComponent: data assente => creazione, data presente => modifica.
 */
@Component({
  selector: 'ebw-monorepo-detail-node',
  templateUrl: './detail-node.component.html',
  styleUrls: ['./detail-node.component.scss']
})
export class DetailNodeComponent implements OnInit, OnDestroy {
  /** Il click sulla mappa per le coordinate è "strong": va acquisito da chi apre il dialog (vedi PsaComponent / PsaObjectViewerComponent) */
  static readonly CLICK_OWNER_ID = 'psa-detail-node';

  nodeForm: FormGroup;
  typesList: any[] = [];
  eventsList: any[] = [];
  private _mapClickSub: Subscription;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any = {},
    private _formBuilder: FormBuilder,
    private _psaService: PsaService,
    private _dialogRef: MatDialogRef<DetailNodeComponent>,
    private _ebwModalService: EbwModalService,
    private _mapService: MapService,
    private _translateService: TranslateService
  ) {
    this._psaService._getEntityTypes('nodo').subscribe((resp) => {
      this.typesList = resp;
    });
  }

  ngOnInit(): void {
    this.nodeForm = this._formBuilder.group({
      nome: ['', [Validators.required, Validators.minLength(3)]],
      tipologia: ['', Validators.required],
      lat: [null, Validators.required],
      lon: [null, Validators.required],
      eventiRischioIds: [[]]
    });

    // click su mappa per popolare lat/lon, attivo per tutta la vita del dialog (stesso pattern di ValveComponent)
    this._mapService.customMapClickEnable$.next(true);
    this._mapClickSub = this._mapService.mapClicked$.subscribe((event) => this._onMapClick(event));

    if (this.data?.id) {
      this.nodeForm.patchValue({
        nome: this.data.search_term || '',
        tipologia: this.data.tipologia || '',
        lat: this.data.lat ?? null,
        lon: this.data.lon ?? null,
        eventiRischioIds: this.data.events?.map((e) => e.evento_rischio_id) || []
      });

      this.nodeForm.get('tipologia')?.disable({ onlySelf: true });
      // in modifica la tipologia è già impostata: la multiselect eventi resta sempre abilitata, filtrata su quella tipologia
      this._loadEventsList(this.data.tipologia);
    } else {
      // in creazione la multiselect eventi resta disabilitata finché non si sceglie la tipologia
      this.nodeForm.get('eventiRischioIds')?.disable();
      this.nodeForm.get('tipologia')?.valueChanges.subscribe((value) => {
        // la tipologia è cambiata: la selezione eventi precedente non è più valida per la nuova tipologia
        this.nodeForm.get('eventiRischioIds')?.setValue([]);
        if (value) {
          this._loadEventsList(value);
          this.nodeForm.get('eventiRischioIds')?.enable();
        } else {
          this.eventsList = [];
          this.nodeForm.get('eventiRischioIds')?.disable();
        }
      });
    }
  }

  private _loadEventsList(tipologia: string): void {
    this._psaService._getListEvents({ tipologia }).subscribe((resp) => {
      this.eventsList = resp;
    });
  }

  private _onMapClick(event: MapBrowserEvent<any>): void {
    if (!event.coordinate?.length) {
      return;
    }
    const coords = transform(event.coordinate, MapService.EPSG_3857_KEY, MapService.EPSG_4326_KEY);
    this.nodeForm.patchValue({ lon: coords[0], lat: coords[1] });
  }

  ngOnDestroy(): void {
    this._mapService.customMapClickEnable$.next(false);
    this._mapClickSub?.unsubscribe();
    // il flag va spento prima di rilasciare il registro (un passive in attesa lo riaccende)
    this._mapService.releaseMapClick(DetailNodeComponent.CLICK_OWNER_ID);
  }

  onCancel(): void {
    this._dialogRef.close();
  }

  async onSave(): Promise<void> {
    try {
      const body = {
        nome: this.nodeForm.value.nome,
        tipologia: this.nodeForm.value.tipologia,
        lat: this.nodeForm.value.lat,
        lon: this.nodeForm.value.lon,
        eventiRischioIds: this.nodeForm.value.eventiRischioIds
      };

      let savedNode: any;
      if (this.data?.id) {
        await lastValueFrom(this._psaService.updateNode(this.data.id, body));
      } else {
        savedNode = await lastValueFrom(this._psaService.addNode(body));
      }

      this._mapService.clearLayerCache();
      const geom = {
        type: 'Point',
        coordinates: this._mapService.transformGeometryCordsFrom4326To3857([body.lon, body.lat])
      };
      this._mapService.centerMapOnGeometry(geom);
      this._ebwModalService.openInfoModal({
        title: this._translateService.instant('map.sidebar.psa.node.save_title'),
        content: this._translateService.instant('common.save_successful')
      });
      if (this.data?.id) {
        this._psaService.nodeChanged$.next();
      }
      this._dialogRef.close(savedNode ?? true);
    } catch (error) {
      console.error('[DetailNode][onSave] ', error);
      this._ebwModalService.openErrorModal({
        title: this._translateService.instant('map.sidebar.psa.node.save_title'),
        content: error?.error?.message
      });
    }
  }
}
